import { AxiosError } from 'axios'

import { Role } from '../interfaces/rolesEnum'
import { api } from '../libs/axios'

type RoleType = keyof typeof Role

export interface User {
  id: string
  avatar_url: string
  email: string
  phone: string
  is_active: boolean
  is_removed: boolean
  name: string
  role: Exclude<RoleType, 'ROOT'>
  subrole?: Exclude<RoleType, 'ROOT'> | null
  manager_of_promoters_company_id: string
  promoter_of_company_id: string
}

export interface GetUsersRequest {
  page?: number
  id?: string
  name?: string
  role?: string
  is_removed?: boolean
}

interface GetUsersResponse {
  users: User[]
  totalCount: number
  pageQty: number
}

export async function getUsers(
  searchParams: GetUsersRequest,
): Promise<GetUsersResponse> {
  try {
    const params: { [key: string]: string | boolean } = {}

    Object.entries(searchParams).forEach(([key, value]) => {
      const param = key.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase()
      if (typeof value === 'boolean') {
        params[param] = value
          ? 'true'
          : 'false'
      } else {
        params[param] = value
      }
    })

    const result = await api.get<GetUsersResponse>('/users', {
      params,
    })

    const { data } = result

    return data
  } catch (err) {
    if (err instanceof AxiosError) {
      console.error(err.message)
    }
    return {
      users: [],
      pageQty: 0,
      totalCount: 0,
    }
  }
}

interface ToggleRemoveUserRequest {
  isRemoved: boolean
  id: string
}

interface ToggleRemoveUserResponse {
  user: {
    id: string
    is_removed: boolean
  }
}

export async function toggleRemoveUser({
  isRemoved,
  id,
}: ToggleRemoveUserRequest) {
  try {
    const url = isRemoved
      ? '/users/restore'
      : '/users/delete'
    const { data } = await api.patch<ToggleRemoveUserResponse>(url, { id })

    return {
      user: data.user,
    }
  } catch (err) {
    if (err instanceof AxiosError) {
      alert('Não foi possível remover o usuário')
    }
  }
}

interface CreateUserRequest {
  email: string
  role: keyof typeof Role
  subrole?: keyof typeof Role | null
  manager_of_promoters_company_id?: string | null
  promoter_of_company_id?: string | null
}

interface CreateUserResponse {
  id: string
  email: string
  phone: string | null
  role: Role
  subrole: Role | null
  is_active: boolean
  manager_of_promoters_company_id: string | null
  promoter_of_company_id: string | null
}

export async function createUser({
  email,
  role,
  subrole,
  manager_of_promoters_company_id,
  promoter_of_company_id,
}: CreateUserRequest, token: string) {
  try {
    const { data } = await api.post<CreateUserResponse>('/users', {
      email,
      role,
      subrole,
      manager_of_promoters_company_id,
      promoter_of_company_id,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    return {
      user: data,
      success: true,
      message: 'Usuário criado com sucesso!',
    }
  } catch (err) {
    if (err instanceof AxiosError) {
      return {
        user: undefined,
        success: false,
        message: err.response?.data.message || 'Não foi possível criar o usuário',
      }
    }
  }
}

interface UpdateUserRequest {
  role: keyof typeof Role
  subrole?: keyof typeof Role | null
  email?: string
  user_id: string
  manager_of_promoters_company_id?: string | null
  promoter_of_company_id?: string | null
}

interface UpdateUserResponse {
  id: string
  email: string
  phone: string | null
  role: Role
  subrole: Role | null
  is_active: boolean
  manager_of_promoters_company_id: string | null
  promoter_of_company_id: string | null
}

export async function updateUser({
  role,
  subrole,
  email,
  manager_of_promoters_company_id,
  promoter_of_company_id,
  user_id,
}: UpdateUserRequest, token: string) {
  try {
    const { data } = await api.put<UpdateUserResponse>('/users', {
      role,
      subrole,
      email,
      user_id,
      manager_of_promoters_company_id,
      promoter_of_company_id,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    return {
      user: data,
      success: true,
      message: 'Usuário atualizado com sucesso!',
    }
  } catch (err) {
    if (err instanceof AxiosError) {
      return {
        user: undefined,
        success: false,
        message: err.response?.data.message || 'Não foi possível atualizar o usuário',
      }
    }
  }
}

interface GetUsersByRoleResponse {
  users: {
    id: string
    name: string
    avatar_url: boolean
    manager_of_promoters_company_id: string
    promoter_of_company_id: string
  }[]
}

export async function getUsersByRole(role: Role): Promise<GetUsersByRoleResponse> {
  try {
    const { data } = await api.get<GetUsersByRoleResponse>(`/users/${role}`)

    return {
      users: data.users,
    }
  } catch (err) {
    if (err instanceof AxiosError) {
      alert('Não foi possível criar o usuário')
    }
  }
  return {
    users: [],
  }
}

interface GetUserProfileResponse {
  user: {
    id: string,
    name: string,
    email: string,
    phone: string,
    avatar_url: string | null,
    role: Role,
    subrole: Role | null,
    manager_of_promoters_company_id: string | null,
    promoter_of_company_id: string | null,
    terms_acceptance: {
      id: string,
      accepted_at: Date | string
      terms: {
        version: string
      }
    }[]
  }
}

export async function getUserProfile() {
  const { data } = await api.get<GetUserProfileResponse>('/me')

  return data.user
}

export interface UpdateProfileRequest {
  id: string
  avatar?: File
  phone?: string
  name?: string
  currentPassword?: string
  newPassword?: string
}

interface UpdateProfileResponse {
  user?: {
    id: string
    name: string
    email: string
    phone: string
    avatar_url: string | null
    role: Role
    subrole: string | null
  },
}

export async function updateProfile(
  body: UpdateProfileRequest,
) {
  try {
    const formData = new FormData()
    Object.entries(body).forEach(([key, value]) => {
      formData.append(key, value)
    })
    const { data } = await api.put<UpdateProfileResponse>('/me', formData)

    return {
      user: data.user,
      success: true,
      message: 'Perfil atualizado!',
    }
  } catch (err) {
    if (err instanceof AxiosError) {
      return {
        user: undefined,
        success: false,
        message: err.response?.data.message || `Não foi possível atualizar o perfil: ${err.message}`,
      }
    }
    console.error(err)
  }
}
